import { Controller } from '@hotwired/stimulus';

// Stripe types (basic)
interface StripeInstance {
  elements: (options: { appearance: { theme: string }; clientSecret: string }) => StripeElements;
  confirmPayment: (options: {
    elements: StripeElements;
    confirmParams: { return_url: string };
  }) => Promise<{ error?: StripeError }>;
}

interface StripeElements {
  create: (type: string, options: { layout: string }) => StripeElement;
}

interface StripeElement {
  mount: (selector: string | HTMLElement) => void;
}

interface StripeError {
  type: string;
  message: string;
}

declare const Stripe: (publishableKey: string) => StripeInstance;

export default class extends Controller<HTMLElement> {
  static targets = ['form', 'submit', 'spinner', 'buttonText', 'message', 'paymentElement'];

  static values = {
    publishableKey: String,
    paymentIntentUrl: String,
    returnUrl: String,
  };

  declare readonly formTarget: HTMLFormElement;
  declare readonly submitTarget: HTMLButtonElement;
  declare readonly spinnerTarget: HTMLElement;
  declare readonly buttonTextTarget: HTMLElement;
  declare readonly messageTarget: HTMLElement;
  declare readonly paymentElementTarget: HTMLElement;

  declare readonly hasFormTarget: boolean;
  declare readonly hasSubmitTarget: boolean;
  declare readonly hasSpinnerTarget: boolean;
  declare readonly hasButtonTextTarget: boolean;
  declare readonly hasMessageTarget: boolean;
  declare readonly hasPaymentElementTarget: boolean;

  declare readonly publishableKeyValue: string;
  declare readonly paymentIntentUrlValue: string;
  declare readonly returnUrlValue: string;

  private stripe!: StripeInstance;
  private elements!: StripeElements;

  async connect(): Promise<void> {
    // Prevent re-initialization on Turbo navigation (element persists across reconnects)
    if (this.element.dataset.stripeInitialized === 'true') {
      return;
    }
    this.element.dataset.stripeInitialized = 'true';

    // Initialize Stripe
    this.stripe = Stripe(this.publishableKeyValue);

    // If the page already loads at $0 (e.g. partner reloads with a fully
    // waiving promo already applied), short-circuit straight to the free
    // form instead of fetching a PaymentIntent that the server will reject.
    if (this.getFinalAmount() === 0) {
      this.handlePromoChange();
    } else {
      await this.initializePayment();
    }

    // Handle promo code changes via HTMX
    document.addEventListener('htmx:afterSwap', (event: Event) => {
      const target = (event as CustomEvent).detail?.target;
      if (target?.id === 'partner-order-summary') {
        this.handlePromoChange();
      }
    });
  }

  private async initializePayment(): Promise<void> {
    try {
      // Build payment intent URL with promo code; unit count is read from session.
      const promoCode = this.getAppliedPromoCode();
      let url = this.paymentIntentUrlValue;
      const params: string[] = [];
      if (promoCode) {
        params.push(`promo_code=${encodeURIComponent(promoCode)}`);
      }
      if (params.length) {
        const separator = url.includes('?') ? '&' : '?';
        url = `${url}${separator}${params.join('&')}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
      });

      const { clientSecret } = await response.json() as { clientSecret: string };

      const appearance = { theme: 'stripe' };
      this.elements = this.stripe.elements({ appearance, clientSecret });

      const paymentElementOptions = { layout: 'accordion' };
      const paymentElement = this.elements.create('payment', paymentElementOptions);
      paymentElement.mount(this.paymentElementTarget);
    } catch (error) {
      this.showMessage('Failed to initialize payment form. Please refresh and try again.');
      console.error('Payment initialization error:', error);
    }
  }

  private getAppliedPromoCode(): string {
    const summary = document.getElementById('partner-order-summary');
    return summary?.dataset.promoCode || '';
  }

  private getFinalAmount(): number {
    const summary = document.getElementById('partner-order-summary');
    return parseInt(summary?.dataset.finalAmount || '1', 10);
  }

  private handlePromoChange(): void {
    const finalAmount = this.getFinalAmount();
    const freeForm = document.getElementById('free-registration-form') as HTMLFormElement | null;
    const promoInput = document.getElementById('free-registration-promo-code') as HTMLInputElement | null;

    if (finalAmount === 0 && freeForm) {
      // 100% discount: hide Stripe form, show free registration
      this.formTarget.classList.add('hidden');
      freeForm.classList.remove('hidden');
      if (promoInput) {
        promoInput.value = this.getAppliedPromoCode();
      }
    } else {
      // Has amount to charge: show Stripe form, hide free registration
      if (freeForm) {
        freeForm.classList.add('hidden');
      }
      this.formTarget.classList.remove('hidden');
      this.paymentElementTarget.innerHTML = '';
      this.initializePayment();
    }
  }

  async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();
    this.setLoading(true);

    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: this.returnUrlValue,
      },
    });

    // Only reached if immediate error (otherwise redirects to return_url)
    if (error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        this.showMessage(error.message);
      } else {
        this.showMessage('An unexpected error occurred.');
      }
      this.setLoading(false);
    }
  }

  private showMessage(messageText: string): void {
    if (!this.hasMessageTarget) return;

    this.messageTarget.classList.remove('hidden');
    this.messageTarget.textContent = messageText;

    setTimeout(() => {
      this.messageTarget.classList.add('hidden');
      this.messageTarget.textContent = '';
    }, 4000);
  }

  private setLoading(isLoading: boolean): void {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = isLoading;
    }

    if (this.hasSpinnerTarget) {
      this.spinnerTarget.classList.toggle('hidden', !isLoading);
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.classList.toggle('hidden', isLoading);
    }
  }
}
